/* Scores, as the redesign draws it: the scored estate as one band bar, the scored datasets worst
 * first, and the chosen one's diagnosis beside the list.
 *
 * **Not computable is a band of its own, and never-scored is counted but not listed.** A score whose
 * total could not be defended is a different fact from a low one, so it gets its own grey segment and
 * a hatched bar on its row rather than a red figure; a dataset nobody has scored is a gap in coverage,
 * said as a count under the bar, because ranking it among scored datasets would rank an absence.
 *
 * **The deeper tools are one step away, not gone.** The diagnosis panel's Investigate opens the
 * dataset's score drawer — why the score changed, run comparison, the rules and records behind it,
 * re-running the evaluation — and Full decomposition opens the dataset's own Score tab.
 *
 * Bands follow the platform's own verdict: a dataset's quality target where an active contract sets
 * one, 90 / 70 otherwise. Two batch reads serve the page, never one per row.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { isAdmin, useIdentity } from '@/entities/session';
import { useDatasetNames, useLatestScores } from '@/features/catalog-browser';
import { useConnections } from '@/features/connections';
import { ageText } from '@/shared/model/age';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SidePanel } from '@/shared/ui/SidePanel';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import { useActiveModel, useDatasetScore } from '../api/scoring';
import { useQualityTargets } from '../api/targets';
import { buildRows } from '../model/rows';
import type { DatasetRow, DrawerTab } from '../model/types';

import { DatasetDrawer } from './DatasetDrawer';
import { ScoreModelEditor } from './ScoreModelEditor';

/** The four bands a scored dataset can sit in, in the bar's order. */
type Band = 'atRisk' | 'watch' | 'healthy' | 'notComputable';

const BANDS: readonly Band[] = ['atRisk', 'watch', 'healthy', 'notComputable'];

/** A scored row's band, or `null` for a dataset nobody has scored. A score that could not be
 * defended is not computable whatever its number says; otherwise the platform's verdict decides. */
function bandOf(row: DatasetRow): Band | null {
  if (row.summary === undefined) return null;
  if (!row.summary.defensible || row.value === null) return 'notComputable';
  if (row.status === 'critical') return 'atRisk';
  if (row.status === 'warning') return 'watch';
  return 'healthy';
}

/** The list's order: not computable first — the evidence gap is the work — then worst to best. */
function byBandThenScore(left: DatasetRow, right: DatasetRow): number {
  const leftOpen = bandOf(left) === 'notComputable';
  const rightOpen = bandOf(right) === 'notComputable';
  if (leftOpen !== rightOpen) return leftOpen ? -1 : 1;
  return (left.value ?? 0) - (right.value ?? 0) || left.name.localeCompare(right.name);
}

function BandCard({
  rows,
  never,
  filter,
  onFilter,
}: {
  readonly rows: readonly DatasetRow[];
  readonly never: number;
  readonly filter: Band | null;
  readonly onFilter: (band: Band | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const counts = new Map(
    BANDS.map((band) => [band, rows.filter((row) => bandOf(row) === band).length]),
  );
  const total = rows.length || 1;

  return (
    <section className="lq-sc__bands" aria-labelledby="scores-bands">
      <div className="lq-sc__bandsHead">
        <h2 id="scores-bands" className="lq-sc__bandsTitle">
          {t('scoring.band.title')}
        </h2>
        <span className="lq-sc__muted">{t('scoring.band.hint')}</span>
        <span className="lq-sc__spacer" />
        {filter === null ? null : (
          <button
            type="button"
            className="lq-sc__clear"
            onClick={() => {
              onFilter(null);
            }}
          >
            {t('scoring.band.showing', { band: t(`scoring.band.${filter}`) })}
          </button>
        )}
      </div>
      <div className="lq-sc__bar">
        {BANDS.map((band) => (
          <button
            key={band}
            type="button"
            className="lq-sc__seg"
            data-band={band}
            data-dim={filter !== null && filter !== band ? 'true' : undefined}
            aria-pressed={filter === band}
            aria-label={t('scoring.band.segment', {
              band: t(`scoring.band.${band}`),
              count: counts.get(band) ?? 0,
            })}
            style={{ width: `${String(((counts.get(band) ?? 0) / total) * 100)}%` }}
            onClick={() => {
              onFilter(filter === band ? null : band);
            }}
          />
        ))}
      </div>
      <div className="lq-sc__legend">
        {BANDS.map((band) => (
          <span key={band} className="lq-sc__key">
            <span className="lq-sc__swatch" data-band={band} />
            <strong>{counts.get(band) ?? 0}</strong> {t(`scoring.band.${band}`)}
          </span>
        ))}
        {never > 0 ? (
          <span className="lq-sc__key">
            <span className="lq-sc__swatch lq-sc__swatch--never" />
            <strong>{never.toLocaleString()}</strong> {t('scoring.band.neverListed')}
          </span>
        ) : null}
      </div>
      <div className="lq-sc__note">{t('scoring.band.note')}</div>
    </section>
  );
}

function ScoreList({
  rows,
  selected,
  connectionNames,
  connectionOf,
  onSelect,
}: {
  readonly rows: readonly DatasetRow[];
  readonly selected: string | null;
  readonly connectionNames: ReadonlyMap<string, string>;
  readonly connectionOf: ReadonlyMap<string, string>;
  readonly onSelect: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const now = new Date();

  return (
    <article className="lq-sc__list">
      {/* The column words are hidden from assistive technology, because the rows are buttons
          rather than a table and each already reads in full; the "i" beside two of them is not,
          so the explanation stays reachable by keyboard and screen reader. */}
      <div className="lq-sc__grid lq-sc__listHead">
        <span aria-hidden="true">{t('scoring.list.dataset')}</span>
        <HelpLabel subject={t('scoring.list.score')} help={t('help.metric.score')} inline>
          <span aria-hidden="true">{t('scoring.list.score')}</span>
        </HelpLabel>
        <span aria-hidden="true">{t('scoring.list.band')}</span>
        <HelpLabel
          subject={t('scoring.list.coverage')}
          help={t('help.metric.coverage')}
          inline
        >
          <span aria-hidden="true">{t('scoring.list.coverage')}</span>
        </HelpLabel>
      </div>
      {rows.length === 0 ? (
        <div className="lq-sc__none">{t('scoring.list.none')}</div>
      ) : (
        <ul className="lq-sc__rows">
          {rows.map((row) => {
            const band = bandOf(row) ?? 'notComputable';
            const summary = row.summary;
            const connection = connectionNames.get(connectionOf.get(row.id) ?? '');
            const when =
              row.evaluatedAt === null ? null : ageText(t, row.evaluatedAt, now);
            return (
              <li key={row.id}>
                <button
                  type="button"
                  className="lq-sc__grid lq-sc__row"
                  aria-pressed={selected === row.id}
                  onClick={() => {
                    onSelect(row.id);
                  }}
                >
                  <span className="lq-sc__nameCell">
                    <span className="lq-sc__name">{row.name}</span>
                    <span className="lq-sc__sub">
                      {[connection, when].filter((part) => part != null).join(' · ')}
                      {row.evidenceComplete ? null : (
                        <span className="lq-sc__partial">
                          {' '}
                          {t('scoring.list.partial')}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="lq-sc__scoreCell">
                    <span
                      className="lq-sc__value"
                      data-computable={band === 'notComputable' ? 'false' : undefined}
                    >
                      {band === 'notComputable' || row.value === null
                        ? t('scoring.band.notComputable')
                        : row.value.toFixed(1)}
                    </span>
                    <span className="lq-sc__track">
                      {band === 'notComputable' || row.value === null ? (
                        <span className="lq-sc__hatch" />
                      ) : (
                        <span
                          className="lq-sc__fill"
                          data-band={band}
                          style={{ width: `${String(row.value)}%` }}
                        />
                      )}
                    </span>
                  </span>
                  <span className="lq-sc__pill" data-band={band}>
                    {t(`scoring.band.${band}`)}
                  </span>
                  <span className="lq-sc__covCell">
                    <span className="lq-sc__cov">
                      {summary === undefined
                        ? '—'
                        : `${String(summary.columns_with_a_rule)}/${String(summary.columns_total)}`}
                    </span>
                    <span className="lq-sc__covTrack">
                      <span
                        className="lq-sc__covFill"
                        style={{
                          width: `${String((summary?.coverage_ratio ?? 0) * 100)}%`,
                        }}
                      />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

/* The chosen dataset's diagnosis: its figure and coverage, which dimension cost the most of the
 * points lost and the costliest rule inside it, and the points lost per dimension. */
function Diagnosis({
  row,
  onInvestigate,
}: {
  readonly row: DatasetRow;
  readonly onInvestigate: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const score = useDatasetScore(row.id);
  const summary = row.summary;
  const computable = summary !== undefined && summary.defensible && row.value !== null;

  const lostBy = new Map<string, number>();
  for (const contribution of score.data?.contributions ?? []) {
    lostBy.set(
      contribution.dimension,
      (lostBy.get(contribution.dimension) ?? 0) + contribution.points_lost,
    );
  }
  const ranked = [...lostBy.entries()].sort((left, right) => right[1] - left[1]);
  const worst = ranked[0];
  const most = worst?.[1] ?? 0;
  const costliest =
    worst === undefined
      ? undefined
      : [...(score.data?.contributions ?? [])]
          .filter((contribution) => contribution.dimension === worst[0])
          .sort((left, right) => right.points_lost - left.points_lost)[0];

  return (
    <aside className="lq-sc__diag" aria-label={t('scoring.diag.label')}>
      <div className="lq-sc__muted">{t('scoring.diag.title')}</div>
      <div className="lq-sc__diagName">{row.name}</div>
      <div className="lq-sc__diagFigure">
        <span className="lq-sc__diagValue">
          {computable ? row.value.toFixed(1) : '—'}
        </span>
        {summary === undefined ? null : (
          <span className="lq-sc__muted">
            {t(
              summary.evidence_complete
                ? 'scoring.diag.coverage'
                : 'scoring.diag.coverageIncomplete',
              { measured: summary.columns_with_a_rule, total: summary.columns_total },
            )}
          </span>
        )}
      </div>

      {computable ? null : (
        <p className="lq-sc__diagText">{t('scoring.diag.notComputable')}</p>
      )}

      {computable && score.isPending ? <span className="lq-sc__shimmer" /> : null}

      {computable &&
      score.isSuccess &&
      worst !== undefined &&
      score.data.value !== null ? (
        <>
          <p className="lq-sc__diagHeadline">
            {t('scoring.diag.headline', {
              dimension: t(`scoring.dimension.${worst[0]}`),
              lost: worst[1].toFixed(1),
              total: (100 - score.data.value).toFixed(1),
            })}
          </p>
          {costliest === undefined ? null : (
            <p className="lq-sc__diagText">
              {t('scoring.diag.costliest', {
                rule: costliest.rule_name,
                lost: costliest.points_lost.toFixed(1),
              })}
            </p>
          )}
          <div className="lq-sc__dims">
            {score.data.dimensions.map((dimension) => {
              const lost = lostBy.get(dimension.dimension) ?? 0;
              return (
                <div key={dimension.dimension} className="lq-sc__dim">
                  <span>{t(`scoring.dimension.${dimension.dimension}`)}</span>
                  <span className="lq-sc__dimTrack">
                    <span
                      className="lq-sc__dimFill"
                      style={{ width: `${String(most > 0 ? (lost / most) * 100 : 0)}%` }}
                    />
                  </span>
                  <span className="lq-sc__dimLost">
                    {lost > 0
                      ? `−${lost.toFixed(1)}`
                      : dimension.value === null
                        ? ''
                        : '0'}
                  </span>
                  <span
                    className="lq-sc__dimValue"
                    data-measured={dimension.value === null ? 'false' : undefined}
                  >
                    {dimension.value !== null
                      ? dimension.value.toFixed(0)
                      : score.data.unmeasurable_dimensions.includes(dimension.dimension)
                        ? t('scoring.diag.unmeasurable')
                        : t('scoring.diag.noEvidence')}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {computable && score.isError ? (
        <p className="lq-sc__diagText">{t('scoring.diag.unreadable')}</p>
      ) : null}

      <div className="lq-sc__diagActions">
        <Link
          className="lq-btn lq-btn--secondary lq-btn--compact"
          to={`/datasets/${row.id}?tab=scores`}
        >
          {t('scoring.diag.full')}
        </Link>
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={onInvestigate}
        >
          {t('scoring.diag.investigate')}
        </button>
      </div>
    </aside>
  );
}

export function ScoresScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const [filter, setFilter] = useState<Band | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [modelOpen, setModelOpen] = useState(false);
  const [drawer, setDrawer] = useState<{
    readonly id: string;
    readonly tab: DrawerTab;
  } | null>(null);

  const names = useDatasetNames();
  const ids = useMemo(() => [...(names.data?.byId.keys() ?? [])], [names.data]);
  const scores = useLatestScores(ids);
  const targets = useQualityTargets();
  const connections = useConnections();
  const model = useActiveModel();

  const rows = useMemo(
    () =>
      buildRows({
        names: names.data?.byId ?? new Map<string, string>(),
        scores: scores.data,
        facts: undefined,
        targets: targets.byDataset,
        movements: new Map(),
        now: Date.now(),
      }),
    [names.data, scores.data, targets.byDataset],
  );

  const scored = useMemo(() => rows.filter((row) => row.summary !== undefined), [rows]);
  const never = rows.length - scored.length;
  const listed = useMemo(
    () =>
      [...scored]
        .filter((row) => filter === null || bandOf(row) === filter)
        .sort(byBandThenScore),
    [scored, filter],
  );
  const connectionNames = useMemo(
    () => new Map((connections.data?.items ?? []).map((each) => [each.id, each.name])),
    [connections.data],
  );

  /* The picked dataset while the filter still shows it, else the first row: the diagnosis is never
     empty while there is a dataset to diagnose, and never describes one the filter has hidden. */
  const selected = listed.find((row) => row.id === picked) ?? listed[0] ?? null;
  const drawerRow =
    drawer === null ? null : (rows.find((row) => row.id === drawer.id) ?? null);

  const header = (
    <PageHeader
      title={t('scoring.title')}
      help={t('help.feature.qualityScore')}
      lede={t('scoring.lede')}
      actions={
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-sc__modelBtn"
          aria-expanded={modelOpen}
          onClick={() => {
            setModelOpen((open) => !open);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" />
            <circle cx="16" cy="6" r="2" />
            <circle cx="10" cy="12" r="2" />
            <circle cx="18" cy="18" r="2" />
          </svg>
          {model.isSuccess
            ? t('scoring.model.openVersion', { version: model.data.version })
            : t('scoring.model.title')}
        </button>
      }
    />
  );

  const modelPanel = modelOpen ? (
    <SidePanel
      label={t('scoring.model.title')}
      onClose={() => {
        setModelOpen(false);
      }}
      head={
        <>
          <h2 className="lq-side__name">
            {model.isSuccess
              ? t('scoring.model.openVersion', { version: model.data.version })
              : t('scoring.model.title')}
          </h2>
          <div className="lq-side__sub">{t('scoring.model.panelLede')}</div>
        </>
      }
    >
      <ScoreModelEditor canPublish={isAdmin(identity.data)} />
    </SidePanel>
  ) : null;

  if (names.isPending || (scores.isPending && ids.length > 0)) {
    return (
      <div className="lq-stack">
        {header}
        <SkeletonRows rows={6} columns={4} />
        {modelPanel}
      </div>
    );
  }

  if (names.isError) {
    return (
      <div className="lq-stack">
        {header}
        <UnavailableState
          title={t('scoring.listing.unreadable')}
          guidance={names.error.message}
        />
        {modelPanel}
      </div>
    );
  }

  return (
    <div className="lq-stack">
      {header}

      {scores.isError ? (
        <UnavailableState
          title={t('scoring.listing.scoresUnreadable')}
          guidance={scores.error.message}
        />
      ) : scored.length === 0 ? (
        <EmptyState
          title={t(
            rows.length === 0
              ? 'scoring.listing.noDatasets'
              : 'scoring.listing.noneScored',
          )}
          guidance={t(
            rows.length === 0
              ? 'scoring.listing.noDatasetsGuidance'
              : 'scoring.listing.noneScoredGuidance',
          )}
        />
      ) : (
        <>
          <BandCard rows={scored} never={never} filter={filter} onFilter={setFilter} />
          <div className="lq-sc__layout">
            <ScoreList
              rows={listed}
              selected={selected?.id ?? null}
              connectionNames={connectionNames}
              connectionOf={names.data.connectionById}
              onSelect={setPicked}
            />
            {selected === null ? null : (
              <Diagnosis
                key={selected.id}
                row={selected}
                onInvestigate={() => {
                  setDrawer({ id: selected.id, tab: 'decomposition' });
                }}
              />
            )}
          </div>
        </>
      )}

      {drawer !== null && drawerRow !== null ? (
        <DatasetDrawer
          row={drawerRow}
          tab={drawer.tab}
          onTab={(tab) => {
            setDrawer({ id: drawer.id, tab });
          }}
          days={30}
          onClose={() => {
            setDrawer(null);
          }}
        />
      ) : null}

      {modelPanel}
    </div>
  );
}
